<template>
  <div class="flex flex-col min-h-screen bg-gray-50">
    <header class="bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-3 shadow-lg">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
        <div class="flex items-center">
          <router-link to="/" class="flex items-center">
            <img src="/img/logo.svg" alt="Free Image Resize Logo" class="w-8 h-8 mr-3" />
            <div>
              <h1 class="text-xl md:text-2xl font-bold tracking-tight">Free Image Resize</h1>
              <p class="text-xs md:text-sm font-light opacity-90">Compress and resize images online for free</p>
            </div>
          </router-link>
        </div>
        
        <nav class="flex items-center">
          <button class="md:hidden p-2 text-white hover:bg-indigo-700 rounded-lg transition-colors" @click="isMenuOpen = !isMenuOpen" aria-label="Toggle menu">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          
          <div class="hidden md:block">
            <a href="https://binaryscript.com/portfolio" target="_blank" class="text-white font-medium hover:text-green-300 transition duration-200 flex items-center bg-indigo-700 bg-opacity-50 py-2 px-4 rounded-lg hover:bg-opacity-70">
              <span>Other Tools</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
          
          <!-- Mobile menu -->
          <div v-if="isMenuOpen" class="absolute top-full left-0 right-0 bg-indigo-700 shadow-xl z-50 md:hidden">
            <div class="p-4">
              <a href="https://binaryscript.com/portfolio" target="_blank" class="block py-3 text-white font-medium hover:text-green-300 transition duration-200 flex items-center">
                <span>Other Tools</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </nav>
      </div>
    </header>
    <main class="flex-1 p-4 md:p-8 max-w-7xl mx-auto w-full">
      <router-view />
    </main>
    <footer class="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-5 mt-auto">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center gap-3">
        <div class="flex flex-wrap justify-center gap-6 md:gap-8">
          <router-link to="/privacy" class="text-white text-sm hover:text-indigo-300 transition">Privacy</router-link>
          <router-link to="/terms" class="text-white text-sm hover:text-indigo-300 transition">Terms</router-link>
          <a href="https://binaryscript.com/portfolio" target="_blank" class="text-white text-sm hover:text-indigo-300 transition flex items-center">
            More Tools
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
        <p class="text-sm opacity-80">&copy; {{ new Date().getFullYear() }} Free Image Resize. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script>
import { useMeta } from 'vue-meta'

export default {
  name: 'App',
  setup() {
    useMeta({
      title: 'Free Image Compression Tool',
      titleTemplate: '%s | Free Image Resize',
      htmlAttrs: {
        lang: 'en',
      },
      meta: [
        { charset: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { name: 'description', content: 'Free tools to compress, resize and optimize your images online. No file upload limits and secure processing.' },
        { name: 'author', content: 'Free Image Resize' },
        { name: 'robots', content: 'index, follow' },
        { name: 'theme-color', content: '#4f46e5' },
        // Open Graph tags
        { property: 'og:site_name', content: 'Free Image Resize' },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: 'https://free-image-resize.com' },
        { property: 'og:title', content: 'Free Online Image Compression Tool' },
        { property: 'og:description', content: 'Free tools to compress, resize and optimize your images online. No file upload limits and secure processing.' },
        { property: 'og:image', content: '/og-image.jpeg' },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        // Twitter Card tags
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Free Online Image Compression Tool' },
        { name: 'twitter:description', content: 'Free tools to compress, resize and optimize your images online.' },
        { name: 'twitter:image', content: '/og-image.jpeg' }
      ],
      link: [
        { rel: 'canonical', href: 'https://free-image-resize.com' },
        { rel: 'icon', href: '/img/favicon.ico' },
        { rel: 'apple-touch-icon', href: '/img/apple-touch-icon.png' }
      ]
    })
    
    // Add structured data for the overall application
    const addSiteStructuredData = () => {
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.innerHTML = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Free Image Resize",
        "url": "https://free-image-resize.com",
        "applicationCategory": "UtilitiesApplication",
        "operatingSystem": "All",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "description": "Free online tools to compress, resize and optimize your images without losing quality. No card required.",
        "potentialAction": {
          "@type": "UseAction",
          "target": "https://free-image-resize.com"
        },
        "image": "/og-image.jpeg"
      })
      document.head.appendChild(script)
    }
    
    // We'll call this function once the component is mounted
    return { addSiteStructuredData }
  },
  data() {
    return {
      isMenuOpen: false
    }
  },
  mounted() {
    // Add structured data for the overall application
    this.addSiteStructuredData()
  },
  watch: {
    $route() {
      // Close mobile menu when route changes
      this.isMenuOpen = false
    }
  }
}
</script>

<style>
/* Global styles */
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

body {
  font-family: 'Inter', sans-serif;
}

/* Add base HTML5 semantic elements styles */
main {
  display: block;
}

article, aside, details, figcaption, figure, 
footer, header, hgroup, menu, nav, section {
  display: block;
}

/* Improve accessibility */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Add focus styles for accessibility */
a:focus, button:focus, input:focus {
  outline: 2px solid #4f46e5;
  outline-offset: 2px;
}

/* Make sure images are responsive */
img {
  max-width: 100%;
  height: auto;
}
</style> 